/**
 * Every tunable in the system, in one place.
 *
 * The rule (from ENGINEERING-STANDARDS.md) is that nothing auditable is a magic
 * number. If you find yourself typing a literal threshold into a handler, it
 * belongs here instead, with a sentence saying what it trades.
 *
 * This file must stay dependency-free: the tracker imports from it, and
 * anything imported here ends up in the 4 KB `t.js` budget.
 *
 * Values that differ per environment (hostnames, secrets, retention) are not
 * here — they are environment variables, listed in CONFIGURATION.md.
 */

// ---------------------------------------------------------------------------
// Tracker (runs on the host page)
// ---------------------------------------------------------------------------

/** Flush when this many events are queued. Higher means fewer requests, more loss on a hard tab kill. */
export const FLUSH_MAX_EVENTS = 10;

/** Flush this long after the first event is queued, even if the batch is not full. */
export const FLUSH_INTERVAL_MS = 2_000;

/** SPA routers often fire replaceState several times for one navigation; collapse bursts within this window. */
export const SPA_DEBOUNCE_MS = 50;

/** Scroll depth is measured at most this often while the user scrolls. Layout reads are not free. */
export const SCROLL_THROTTLE_MS = 250;

/** First pageview waits for the browser to be idle, but never longer than this. */
export const IDLE_TIMEOUT_MS = 2_000;

/** Fraction of uncaught errors reported. Errors arrive in storms; one in ten is plenty to spot a problem. */
export const ERROR_SAMPLE_RATE = 0.1;

/** Hard cap on error events from one page load, whatever the sample rate says. */
export const ERROR_MAX_PER_PAGE = 5;

/** Link targets with these extensions are recorded as downloads rather than navigation. */
export const DOWNLOAD_EXTENSIONS = [
  'pdf', 'zip', 'docx', 'doc', 'xlsx', 'xls', 'csv', 'pptx', 'ppt', 'mp4', 'mov', 'dmg', 'exe', 'msi',
] as const;

/** Browsers refuse sendBeacon payloads around 64 KB. Stay comfortably under it. */
export const TRACKER_MAX_PAYLOAD_BYTES = 60_000;

/** Gzipped size ceiling for t.js. CI fails above this. See the performance budget in PLANNING.md. */
export const TRACKER_SIZE_BUDGET_BYTES = 4_096;

// ---------------------------------------------------------------------------
// Ingest Worker
// ---------------------------------------------------------------------------

/** How long an ingest isolate trusts its in-memory copy of a site record before re-reading KV. */
export const SITE_CACHE_TTL_MS = 60_000;

/** Request bodies larger than this are dropped unread. */
export const INGEST_MAX_BODY_BYTES = 64 * 1024;

/** Events per beacon. A tracker flushes at FLUSH_MAX_EVENTS, so anything far above that is not ours. */
export const INGEST_MAX_EVENTS_PER_BATCH = 50;

/** Cloudflare bot scores run 1 (bot) to 99 (human). Below this we flag the event as bot traffic. */
export const BOT_SCORE_THRESHOLD = 30;

/** Field length limits, applied at ingest so the database never sees an unbounded string. */
export const FIELD_LIMITS = {
  eventName: 64,
  path: 2_048,
  query: 2_048,
  referrer: 2_048,
  hostname: 255,
  userRef: 128,
  accountRef: 128,
  utm: 255,
  propsBytes: 4_096,
  language: 35,
} as const;

/**
 * t.js caching. One hour is the ceiling on how long a tracker fix takes to
 * reach every visitor; stale-while-revalidate keeps it free in the meantime.
 */
export const TRACKER_CACHE_CONTROL = 'public, max-age=3600, stale-while-revalidate=86400';

// ---------------------------------------------------------------------------
// Database access
// ---------------------------------------------------------------------------

/** A Supabase RPC that has not answered in this long has failed. The queue retries; the dashboard shows an error. */
export const RPC_TIMEOUT_MS = 10_000;

/** Account bucket for identified usage whose account cannot be resolved. Parentheses cannot occur in a real ref. */
export const UNATTRIBUTED_ACCOUNT = '(unattributed)';

/**
 * Session-level dimensions available in reports. The SQL in refresh_rollups is
 * where each is derived; this list is what the API accepts. A test asserts the
 * two agree.
 */
export const DIMENSIONS = ['referrer', 'utm_source', 'utm_medium', 'utm_campaign', 'country', 'device', 'browser', 'os'] as const;
export type Dimension = (typeof DIMENSIONS)[number];

// ---------------------------------------------------------------------------
// Sessions, rollups and reports (passed into SQL as parameters)
// ---------------------------------------------------------------------------

/** A gap longer than this between two events from the same visitor starts a new session. */
export const SESSION_GAP_MINUTES = 30;

/**
 * Days are London days. Partitions are UTC days (a storage concern), but every
 * report a person reads buckets by the calendar they live in, including across
 * the BST changeover.
 */
export const REPORT_TIMEZONE = 'Europe/London';

/** The rollup cron recomputes this many recent days on every run. Cheap, idempotent, and covers late events. */
export const ROLLUP_RECOMPUTE_DAYS = 2;

/**
 * The two cron schedules in workers/processor/wrangler.jsonc. The Worker
 * dispatches on the exact string. The five-minute one is written as a range
 * rather than star-slash-5: same interval, but a distinct schedule, so a deploy
 * re-registers it when Cloudflare has stopped dispatching the previous one.
 */
export const CRON_FREQUENT = '2-59/5 * * * *';
export const CRON_NIGHTLY = '15 2 * * *';

/**
 * KV key where the processor publishes its effective settings every run, so
 * the dashboard's settings screen shows what is actually running rather than
 * a second copy of the configuration that could drift.
 */
export const PROCESSOR_SETTINGS_KEY = 'config:processor';

/** Event partitions are created this far ahead of today so ingest never meets a missing partition. */
export const PARTITION_DAYS_AHEAD = 7;

/** The "right now" window on the real-time view. */
export const REALTIME_WINDOW_MINUTES = 30;

/** Longest date range a report will accept. */
export const REPORT_MAX_RANGE_DAYS = 400;

/** Default raw-event retention when RAW_EVENT_RETENTION_DAYS is unset. 0 means never drop. */
export const DEFAULT_RAW_EVENT_RETENTION_DAYS = 400;

// ---------------------------------------------------------------------------
// Account health
// ---------------------------------------------------------------------------

/** An account whose active users fall by at least this fraction period-on-period needs attention. */
export const HEALTH_DROP_THRESHOLD = 0.4;

/** Below this many active users in the earlier period, a percentage drop is noise, not a signal. */
export const HEALTH_MIN_BASELINE_USERS = 3;

/** No activity for this many days and an account is "gone quiet". */
export const HEALTH_QUIET_AFTER_DAYS = 14;

// ---------------------------------------------------------------------------
// Admin authentication
// ---------------------------------------------------------------------------

/**
 * PBKDF2-SHA256 iterations for the admin password hash.
 *
 * OWASP would like more. Cloudflare Workers' WebCrypto refuses PBKDF2 above
 * 100,000 iterations, so this is the platform ceiling rather than a choice.
 * It is a stopgap for the single hard-coded admin until Entra ID replaces it.
 */
export const PBKDF2_ITERATIONS = 100_000;

/** Admin session lifetime. One working day. */
export const ADMIN_SESSION_TTL_SECONDS = 8 * 60 * 60;

/** Name of the admin session cookie. */
export const ADMIN_SESSION_COOKIE = 'ta_session';

/** Failed logins allowed per IP per window before the API starts refusing. Backstop for a WAF rule. */
export const LOGIN_MAX_ATTEMPTS = 5;

/** The window for LOGIN_MAX_ATTEMPTS. */
export const LOGIN_WINDOW_MS = 15 * 60 * 1000;

/**
 * Clock skew allowed when checking a provider token's exp, nbf and iat. Our clock and Microsoft's
 * disagree by a few seconds at most; a minute absorbs that without stretching a token's life much.
 */
export const AUTH_CLOCK_TOLERANCE_SECONDS = 60;

/** An identity provider's token or key endpoint that has not answered in this long has failed. */
export const AUTH_HTTP_TIMEOUT_MS = 10_000;

/**
 * How long a sign-in may take between leaving for the provider and coming back. Long enough to
 * type a password and approve multi-factor; short enough that a stolen transaction cookie is stale.
 */
export const AUTH_TRANSACTION_TTL_SECONDS = 600;

/** Cookie carrying state, nonce and the PKCE verifier across the provider round trip. */
export const AUTH_TRANSACTION_COOKIE = 'ta_oauth';

/** Shortest session SESSION_TTL_SECONDS may set. Below this, people re-authenticate constantly. */
export const MIN_SESSION_TTL_SECONDS = 300;

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

/** How often the real-time and install-status screens poll while visible. */
export const DASHBOARD_POLL_MS = 5_000;
